import {
	Breakpoints,
	Container,
	ContainerPadding,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
	Image,
	CTA,
	ContainerGutter,
	BreakpointWrapper,
} from '@volkswagen-onehub/components-core';

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { PortalContext } from '../../context/PortalContext';
import { getUserName } from '../../helpers/nameHelper';
import { CarPlusIcon } from '../vehicleManager/utils/car-plus.icon';
import { HelpContainer } from '../vehicleManager/components/help-container';
import { CreateVehicle } from '../vehicleManager/components/create-vehicle';
import { ChevronLeft } from '@volkswagen-onehub/icons-core';
import { ErrorType, Vehicle } from '@esportal/portal-service';
import { useTrackingManager } from '../../tracking/useTrackingManager';
import { AlertVehicleManagerLayer } from '../vehicleManager/layer/alertVehicleManagerLayer';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import { formatOwnership, pageInPath } from '../../tracking/trackingHelper';

const StyledSection = styled.section`
	align-content: center;

	@media screen and (min-width: 560px) {
		min-height: 75vh;
	}

	@media screen and (min-width: 960px) {
		min-height: 80vh;
	}

	@media screen and (min-width: 1280px) {
		min-height: 70vh;
	}
`;

const ClickableContainer = styled.div`
	cursor: pointer;
`;

const CTAStyledAdd = styled(CTA)`
	width: 64px;
	height: 64px;

	& svg {
		width: 32px;
		height: 32px;
	}
`;

const CTAStyledOriginal = styled(CTA)`
	color: #6a767d;
`;

export const AddCarClub = ({
	vehicles,
	setErrorType,
}: {
	setErrorType: React.Dispatch<React.SetStateAction<ErrorType>>;
	vehicles: Vehicle[];
}): React.ReactElement => {
	const { portalService } = React.useContext(PortalContext);

	const trackingManager = useTrackingManager();

	const userData = portalService.getPerson().getValue();
	const userName = getUserName(userData);
	const alertLayerRef = React.useRef<LayerHandleV2<any>>(null);
	const [createVehicle, setCreateVehicle] = React.useState(false);
	const [isAlertLayerVisible, setIsAlertLayerVisible] = React.useState<boolean>(false);

	useEffect(() => {
		pageInPath('no-vehicles.app');
		trackingManager.trackClubVWGenericFeatureAppLoad(
			{ contentId: 'no-vehicles' },
			{},
			{ vehicle: formatOwnership(vehicles) },
		);
	}, []);

	const handleAddVehicleOnClick = () => {
		trackingManager.trackClubVWButtonClick(
			'Añadir coche',
			{ contentId: 'No Vehicles - Add Vehicles' },
			{ vehicle: formatOwnership(vehicles) },
		);

		setCreateVehicle(true);
	};

	return (
		<>
			<AlertVehicleManagerLayer
				setIsAlertLayerVisible={setIsAlertLayerVisible}
				isAlertLayerVisible={isAlertLayerVisible}
				alertLayerRef={alertLayerRef}
				errorType={ErrorType.AddVehicleClub}
			/>
			{createVehicle ? (
				<CreateVehicle
					setIsAlertLayerVisible={setIsAlertLayerVisible}
					layerRef={alertLayerRef}
					registerToClub={true}
					setErrorType={setErrorType}
				/>
			) : (
				<Container
					wrap={'always'}
					padding={{ top: ContainerPadding.dynamic0200, bottom: ContainerPadding.dynamic0200 }}
				>
					<Layout
						data-testid="add-car-club__layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 7 },
								{ name: 'a', columns: 10 },
								{ name: '.', columns: 7 },
							],
							[Breakpoints.b560]: [
								{ name: '.', columns: 8 },
								{ name: 'a', columns: 8 },
								{ name: '.', columns: 8 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 10 },
								{ name: 'a', columns: 4 },
								{ name: '.', columns: 10 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 10 },
								{ name: 'a', columns: 4 },
								{ name: '.', columns: 10 },
							],
							[Breakpoints.b1600]: [
								{ name: '.', columns: 10 },
								{ name: 'a', columns: 4 },
								{ name: '.', columns: 10 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 11 },
								{ name: 'a', columns: 2 },
								{ name: '.', columns: 11 },
							],
							[Breakpoints.b2560]: [
								{ name: '.', columns: 11 },
								{ name: 'a', columns: 2 },
								{ name: '.', columns: 11 },
							],
						}}
						allowOverflowingContent
					>
						<Container padding={{ bottom: ContainerPadding.dynamic0250 }} horizontalAlign={'center'} stretchContent>
							<Image
								src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-club/logo_club.svg"
								alt="logoClub"
							/>
						</Container>
					</Layout>
					<StyledSection className="add-car-club__section">
						<Layout
							data-testid="add-car-club__layout"
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 0 },
									{ name: 'a', columns: 24 },
									{ name: '.', columns: 0 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b560]: [
									{ name: '.', columns: 0 },
									{ name: 'a', columns: 24 },
									{ name: '.', columns: 0 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b960]: [
									{ name: '.', columns: 0 },
									{ name: 'a', columns: 24 },
									{ name: '.', columns: 0 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b1280]: [
									{ name: '.', columns: 0 },
									{ name: 'a', columns: 24 },
									{ name: '.', columns: 0 },
									{ name: '.', columns: 3 },
									{ name: 'b', columns: 18 },
									{ name: '.', columns: 3 },
								],
								[Breakpoints.b1600]: [
									{ name: '.', columns: 0 },
									{ name: 'a', columns: 24 },
									{ name: '.', columns: 0 },
									{ name: '.', columns: 5 },
									{ name: 'b', columns: 14 },
									{ name: '.', columns: 5 },
								],
								[Breakpoints.b1920]: [
									{ name: '.', columns: 0 },
									{ name: 'a', columns: 24 },
									{ name: '.', columns: 0 },
									{ name: '.', columns: 6 },
									{ name: 'b', columns: 12 },
									{ name: '.', columns: 6 },
								],
								[Breakpoints.b2560]: [
									{ name: '.', columns: 0 },
									{ name: 'a', columns: 24 },
									{ name: '.', columns: 0 },
									{ name: '.', columns: 7 },
									{ name: 'b', columns: 10 },
									{ name: '.', columns: 7 },
								],
							}}
							allowOverflowingContent
						>
							<Container stretchContent>
								<Layout
									data-testid="add-car-club__layout-content"
									appearance={{
										[Breakpoints.default]: [
											{ name: '.', columns: 1 },
											{ name: 'a', columns: 22 },
											{ name: '.', columns: 1 },
											{ name: '.', columns: 6 },
											{ name: 'b', columns: 12 },
											{ name: '.', columns: 6 },
											{ name: '.', columns: 2 },
											{ name: 'c', columns: 20 },
											{ name: '.', columns: 2 },
											{ name: '.', columns: 1 },
											{ name: 'd', columns: 22 },
											{ name: '.', columns: 1 },
										],
										[Breakpoints.b560]: [
											{ name: '.', columns: 1 },
											{ name: 'a', columns: 22 },
											{ name: '.', columns: 1 },
											{ name: '.', columns: 8 },
											{ name: 'b', columns: 8 },
											{ name: '.', columns: 8 },
											{ name: '.', columns: 3 },
											{ name: 'c', columns: 18 },
											{ name: '.', columns: 3 },
											{ name: '.', columns: 6 },
											{ name: 'd', columns: 12 },
											{ name: '.', columns: 6 },
										],
										[Breakpoints.b960]: [
											{ name: '.', columns: 2 },
											{ name: 'a', columns: 20 },
											{ name: '.', columns: 2 },
											{ name: '.', columns: 9 },
											{ name: 'b', columns: 6 },
											{ name: '.', columns: 9 },
											{ name: '.', columns: 6 },
											{ name: 'c', columns: 12 },
											{ name: '.', columns: 6 },
											{ name: '.', columns: 8 },
											{ name: 'd', columns: 8 },
											{ name: '.', columns: 8 },
										],
										[Breakpoints.b1280]: [
											{ name: '.', columns: 4 },
											{ name: 'a', columns: 16 },
											{ name: '.', columns: 4 },
											{ name: '.', columns: 10 },
											{ name: 'b', columns: 4 },
											{ name: '.', columns: 10 },
											{ name: '.', columns: 8 },
											{ name: 'c', columns: 8 },
											{ name: '.', columns: 8 },
											{ name: '.', columns: 9 },
											{ name: 'd', columns: 6 },
											{ name: '.', columns: 9 },
										],
										[Breakpoints.b1600]: [
											{ name: '.', columns: 5 },
											{ name: 'a', columns: 14 },
											{ name: '.', columns: 5 },
											{ name: '.', columns: 10 },
											{ name: 'b', columns: 4 },
											{ name: '.', columns: 10 },
											{ name: '.', columns: 8 },
											{ name: 'c', columns: 8 },
											{ name: '.', columns: 8 },
											{ name: '.', columns: 9 },
											{ name: 'd', columns: 6 },
											{ name: '.', columns: 9 },
										],
										[Breakpoints.b1920]: [
											{ name: '.', columns: 6 },
											{ name: 'a', columns: 12 },
											{ name: '.', columns: 6 },
											{ name: '.', columns: 10 },
											{ name: 'b', columns: 4 },
											{ name: '.', columns: 10 },
											{ name: '.', columns: 8 },
											{ name: 'c', columns: 8 },
											{ name: '.', columns: 8 },
											{ name: '.', columns: 10 },
											{ name: 'd', columns: 4 },
											{ name: '.', columns: 10 },
										],
										[Breakpoints.b2560]: [
											{ name: '.', columns: 7 },
											{ name: 'a', columns: 10 },
											{ name: '.', columns: 7 },
											{ name: '.', columns: 11 },
											{ name: 'b', columns: 2 },
											{ name: '.', columns: 11 },
											{ name: '.', columns: 8 },
											{ name: 'c', columns: 8 },
											{ name: '.', columns: 8 },
											{ name: '.', columns: 10 },
											{ name: 'd', columns: 4 },
											{ name: '.', columns: 10 },
										],
									}}
									allowOverflowingContent
								>
									<Container wrap={'always'} padding={{ bottom: ContainerPadding.dynamic0250 }} stretchContent>
										<Container
											padding={{ bottom: ContainerPadding.dynamic0130 }}
											horizontalAlign={'center'}
											stretchContent
										>
											<Text appearance={TokenTextAppearance.headline400} textAlign={TextAlignment.center} bold>
												¡Hola, {userName}!
											</Text>
										</Container>

										{/* <BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b560}>
											<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
												Parece que no tenemos ningún coche asociado a tu perfil.
											</Text>
											<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
												¿Es así?
											</Text>
										</BreakpointWrapper> */}
										<BreakpointWrapper min={Breakpoints.default}>
											<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
												Añade tu Volkswagen para empezar a disfrutar de las ventajas del club.
											</Text>
										</BreakpointWrapper>
									</Container>

									<Container
										wrap={'never'}
										padding={{ bottom: ContainerPadding.dynamic0050 }}
										gutter={ContainerGutter.static250}
										verticalAlign={'center'}
										horizontalAlign={'center'}
									>
										<ClickableContainer onClick={handleAddVehicleOnClick}>
											<Text appearance={TokenTextAppearance.bigcopy100}>Añadir coche</Text>
										</ClickableContainer>
										<CTAStyledAdd
											tag="button"
											emphasis="primary"
											onClick={handleAddVehicleOnClick}
											aria-label="Añadir coche"
											icon={<CarPlusIcon />}
										/>
									</Container>

									<Container wrap={'always'} padding={{ bottom: ContainerPadding.dynamic0130 }} stretchContent>
										<Container
											wrap={'always'}
											padding={{ bottom: ContainerPadding.dynamic0250 }}
											horizontalAlign={'center'}
											stretchContent
										>
											<Text
												appearance={TokenTextAppearance.label150}
												textAlign={TextAlignment.center}
												color={TokenTextColor.secondary}
											>
												Si tu Volkswagen está en camino, podrás añadirlo después de la entrega. Y si tienes una furgo
												Volkswagen, ¡te hemos reservado un hueco en nuestra comunidad{' '}
												<CTAStyledOriginal
													tag="a"
													href="https://www.theoriginals.es"
													emphasis="tertiary"
													aria-label="The Originals"
												>
													<Text appearance={TokenTextAppearance.label150} color={TokenTextColor.secondary}>
														The Originals!
													</Text>
												</CTAStyledOriginal>{' '}
											</Text>
										</Container>
										<Text appearance={TokenTextAppearance.headline100} textAlign={TextAlignment.center}>
											Si no lo tienes, pero lo quieres...
										</Text>
									</Container>

									<Container
										wrap={'always'}
										horizontalAlign={'center'}
										verticalAlign={'center'}
										padding={{ bottom: ContainerPadding.dynamic0250 }}
										stretchContent
									>
										<Text textAlign={TextAlignment.center}>
											<CTA
												tag="a"
												emphasis="primary"
												aria-label="vamos"
												size="large"
												href="https://www.volkswagen.es/es/modelos.html"
												target="_blank"
												stretchContent
												onClick={() => {
													trackingManager.trackClubVWButtonClick(
														'Encuentra tu Volkswagen',
														{
															contentId: 'Open Volkswagen website',
														},
														{ vehicle: formatOwnership(vehicles) },
													);
												}}
											>
												Encuentra tu Volkswagen
											</CTA>
										</Text>

										<Container
											wrap={'never'}
											padding={{ top: ContainerPadding.dynamic0100 }}
											gutter={ContainerGutter.static150}
											horizontalAlign={'center'}
											verticalAlign={'flex-start'}
										>
											<CTA tag="a" emphasis="tertiary" size="large" href="https://www.volkswagen.es/">
												<Text appearance={TokenTextAppearance.headline100} textAlign={TextAlignment.center} bold>
													<ChevronLeft variant="default" />
												</Text>
											</CTA>
											<CTA
												tag="a"
												emphasis="tertiary"
												size="large"
												href="https://www.volkswagen.es/"
												onClick={() => {
													trackingManager.trackClubVWLinkClick(
														'Volver a la web',
														{
															contentId: 'Navigate to Volkswagen website',
														},
														{ vehicle: formatOwnership(vehicles) },
													);
												}}
											>
												<Text appearance={TokenTextAppearance.headline100} textAlign={TextAlignment.center} bold>
													Volver a la web
												</Text>
											</CTA>
										</Container>
									</Container>
								</Layout>
							</Container>
							<Container stretchContent>
								<HelpContainer />
							</Container>
						</Layout>
					</StyledSection>
				</Container>
			)}
		</>
	);
};
